import { Helmet } from 'react-helmet'
import { REACT_APP_INTERCOM_APP_ID } from 'env'
import { useAuth } from 'hooks/UseAuth'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const nowTimestamp = () => Math.round(new Date().getTime() / 1000)

export const Intercom = observer(() => {
  const auth = useAuth()
  const location = useLocation()
  const isNewRegistration = useFlipperFlag('new_registration')
  const workspace = useCurrentWorkspace()
  const { isIOSNative } = useResponsiveDesign()

  const isOnboardingRoute =
    location.pathname.includes('/virtual-marketer-onboarding') ||
    location.pathname.includes('/vm-onboarding-test') ||
    location.pathname === '/signup'

  const intercomRageHookScript = `(function(exports){'use strict';function retryWithBackoff(condition, callback, maxWait, failureMsg) {if (maxWait === void 0) { maxWait = 4000; }var wait = 64;var resultFn = function () {if (condition()) {callback();return;}wait *= 2;if (wait > maxWait) {var fs = window[window['_fs_namespace']];if (fs && failureMsg) {fs.log('warn', failureMsg);}return;}setTimeout(resultFn, wait);};return resultFn;}var threshold = 0;var prepopulatedMessage = 'If you are having difficulty, feel free to let us know by messaging our team.';var errorMessage = 'FullStory-Intercom frustration events enabled, but Intercom is not defined';var storageKey = '_fs_intercomFrustrationCount';function intercomOnPage(printError) {var onPage = window['Intercom'] !== undefined &&typeof window['Intercom'] === 'function';if (!onPage && printError) {console.error(errorMessage);}return onPage;}function addEventListeners() {if (intercomOnPage(true)) {['fullstory/rageclick'].forEach(function (event) {window.addEventListener(event, trackFrustrationEvent);window.addEventListener(event, showPrepopulatedMessage);});}}function trackFrustrationEvent(event) {var type = event.type;var eventReplayUrlAtCurrentTime = event.detail.eventReplayUrlAtCurrentTime;var count = parseInt(sessionStorage.getItem(storageKey));sessionStorage.setItem(storageKey, isNaN(count) ? '1' :(count + 1).toString());if (intercomOnPage(true)) {window.Intercom('trackEvent', type, {'Session Link': {value: 'Play in FullStory',url: eventReplayUrlAtCurrentTime}});}}function showPrepopulatedMessage() {if (intercomOnPage(true) &&threshold.toString() == sessionStorage.getItem(storageKey)) {window.Intercom('showNewMessage', prepopulatedMessage);}}function run() {retryWithBackoff(intercomOnPage, addEventListeners, 4000, errorMessage)();}exports.addEventListeners = addEventListeners;exports.intercomOnPage = intercomOnPage;exports.run = run;exports.showPrepopulatedMessage = showPrepopulatedMessage;exports.trackFrustrationEvent = trackFrustrationEvent;return exports;}({}));`

  // Handle shutdown/boot when is_ap_freemium changes
  useEffect(() => {
    if (typeof window.Intercom !== 'function') {
      return
    }

    if (workspace?.is_ap_freemium) {
      window.Intercom('shutdown')
    } else {
      if (auth && !isIOSNative && window.intercomSettings) {
        window.Intercom('boot', window.intercomSettings)
      }
    }
  }, [workspace?.is_ap_freemium, isIOSNative, auth, isNewRegistration])

  // Handle updates when on non-freemium workspace
  useEffect(() => {
    if (auth && typeof window.Intercom === 'function' && !workspace?.is_ap_freemium) {
      window.Intercom('update', {
        email: auth.email,
        last_request_at: nowTimestamp(),
        is_freemium: isNewRegistration,
      })

      if (isOnboardingRoute) {
        window.Intercom('hideNotifications', true)
      } else {
        window.Intercom('hideNotifications', false)
      }
    }
  }, [auth, location, isNewRegistration, workspace?.is_ap_freemium, isOnboardingRoute])

  if (isIOSNative) {
    return null
  }

  if (!auth) {
    return (
      <>
        <Helmet>
          <script>
            {`
            window.intercomSettings = {
              app_id: "${REACT_APP_INTERCOM_APP_ID}",
              hide_default_launcher: true,
              custom_launcher_selector: "#intercom_launcher",
              last_request_at: "${nowTimestamp()}",
              is_freemium: ${isNewRegistration}
            };
          `}
          </script>
        </Helmet>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <script>
          {`
          window.intercomSettings = {
            app_id: "${REACT_APP_INTERCOM_APP_ID}",
            name: "${auth.first_name}",
            email: "${auth.email}",
            hide_default_launcher: true,
            custom_launcher_selector: "#intercom_launcher",
            user_hash: "${auth.intercom_hash}",
            last_request_at: "${nowTimestamp()}",
            is_freemium: ${isNewRegistration}
          };
      `}
        </script>
        <script>{intercomRageHookScript}</script>
      </Helmet>
    </>
  )
})
